import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups';
import { CreateMemoryGameSelectTypeTemplate } from '../../components/Templates';
import { getWebBoosterVairant } from '../../constants/webBoosterType';
import { usePopup } from '../../context';
import useGameCurrency from '../../hooks/useGameCurrency';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { TranslationLocale } from '../../i18n';
import { TtsLocale, UiLocale } from '../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { getUiLocale, uiToTranslation } from '../../utils/i18n';
import { getWritingLocaleByRegion } from '../../utils/region';

const CreateMemoryGameSelectTypeScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );

  useRedirectEntry();

  const activeRegion = useSessionStore((store) => store.activeRegion);

  useEffect(() => {
    useDraftStore
      .getState()
      .setLocale(getUiLocale(i18n.language as TranslationLocale));
  }, [i18n.language]);

  const locale = getUiLocale(i18n.language as TranslationLocale) as TtsLocale;

  const choosenLocale = useDraftStore((store) => store.getLocale());
  const recentLocale = useDraftStore((store) => store.getRecentAnswerLocale());

  useEffect(() => {
    i18n
      .loadLanguages([uiToTranslation(choosenLocale as UiLocale)])
      .catch(console.error);
  }, [i18n, locale, choosenLocale]);

  const onClickConfirmType = useCallback(() => {
    useDraftStore.getState().clearGameDraft();
    useDraftStore
      .getState()
      .addGameDraft(t, userAccount?.settings.webBoosterType);
    navigate('/create/memory-game/edit-game', {
      state: { index: 0, type: 'Answer', fromPage: 'selectType' },
    });
  }, [navigate, t, userAccount?.settings.webBoosterType]);

  const handleLanguagePopup = useCallback(
    (gameMode: QuickCreateGameMode, selectedLocale: TtsLocale) => {
      const { languageList } = getQuickCreateGameModeProps(
        gameMode,
        userAccount?.settings.webBoosterType,
      );

      showPopup(
        <QuestionAnswerLanguageSelectPopup
          title={`Language of Answer (${
            getWebBoosterVairant(userAccount?.settings.webBoosterType)
              .shortCreateName
          })`}
          languageList={languageList}
          selectedLanguage={selectedLocale}
          recentLanguage={recentLocale}
          onClickOK={() => {
            hidePopup();
            onClickConfirmType();
          }}
          onCancel={hidePopup}
          onSelectLanguage={(language: TtsLocale) => {
            useDraftStore.getState().setLocale(language);
            useDraftStore.getState().setRecentAnswerLocale(language);
            handleLanguagePopup(gameMode, language); // Re-show popup with updated locale
          }}
        />,
      );
    },
    [
      hidePopup,
      onClickConfirmType,
      recentLocale,
      showPopup,
      userAccount?.settings.webBoosterType,
    ],
  );

  // Handler for selecting a game type
  const onClickGameType = useCallback(
    (gameMode: QuickCreateGameMode) => {
      useDraftStore.getState().setGameMode(gameMode);

      const { languageList, defaultLocale: fallbackLocale } =
        getQuickCreateGameModeProps(
          gameMode,
          userAccount?.settings.webBoosterType,
        );

      // Determine the default locale
      let selectedLocale = recentLocale;
      if (languageList && !languageList.includes(recentLocale)) {
        selectedLocale =
          gameMode === QuickCreateGameMode.HandWriting && activeRegion
            ? getWritingLocaleByRegion(activeRegion)
            : fallbackLocale;
        useDraftStore.getState().setLocale(selectedLocale);
        useDraftStore.getState().setRecentAnswerLocale(selectedLocale);
      }

      handleLanguagePopup(gameMode, selectedLocale);
    },
    [
      userAccount?.settings.webBoosterType,
      recentLocale,
      handleLanguagePopup,
      activeRegion,
    ],
  );

  const allowance = useUserStore((store) => store.getAllowance());

  const { token, creationStamina, showCurrencyDetailPopup } = useGameCurrency(
    userAccount,
    userProfile,
    allowance,
  );

  return (
    <CreateMemoryGameSelectTypeTemplate
      // onGuideClick={onGuideClick}
      webBoosterType={userAccount?.settings.webBoosterType}
      onClickType={(mode) => onClickGameType(mode)}
      onClickCurrency={showCurrencyDetailPopup}
      token={token}
      creationStamina={creationStamina}
      profile={userProfile}
    />
  );
};
export default CreateMemoryGameSelectTypeScreen;
